// The ear's first half: record one utterance, and make it small.
//
// Palefò bills recognition by the BYTES of audio it is sent (measured: about
// 94 bytes a token, whatever the codec), so everything here is about sending
// less: 16 kHz mono, the silence either side of the voice cut off, and MP3
// rather than WAV — the same words for roughly an eighth of the price.
// Recording ends on the silence after speech, on a tap, or at MAX_MS.
//
// Adapted from pratik/js/asr.js, the family's recorder.

import { audioContext } from './audio';

const RATE = 16000;
const MAX_MS = 8000;
const WAIT_MS = 5000;          // nothing said at all: give up
const QUIET_MS = 1200;         // the pause that ends an utterance — long enough for a breath between "twa san" and "dola"
const MIN_SPEECH_MS = 250;
const SPEECH_RMS = 0.015;
const TRIM_RMS = 0.008, TRIM_WIN = 0.02, TRIM_PAD = 0.2;   // seconds
const MP3_KBPS = 32;

let stream = null;

/** Why the microphone cannot be used here: '' | 'insecure' | 'unsupported'. */
export function micProblem() {
  if (!window.isSecureContext) return 'insecure';
  if (!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)) return 'unsupported';
  return '';
}

/** Let the microphone go: the OS indicator turns off, the permission stays. */
export function releaseMic() {
  try { stream?.getTracks().forEach((t) => t.stop()); } catch {}
  stream = null;
}

async function getStream() {
  if (stream?.active) return stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
  } catch (e) {
    throw new Error(e?.name === 'NotFoundError' ? 'mic_missing' : 'mic_denied');
  }
  return stream;
}

/**
 * Record one utterance. `onLevel(0..1)` feeds the mic animation; aborting
 * `signal` ends the recording early and keeps what was said.
 * Resolves { audioBase64, format }, or null when nothing was said.
 */
export async function record({ onLevel, signal } = {}) {
  const ctx = audioContext();
  if (!ctx) throw new Error('mic_unsupported');
  const input = await getStream();
  const blob = await capture(ctx, input, { onLevel, signal });
  if (!blob) return null;
  const samples = trimSilence(await toMono16k(ctx, blob));
  return encode(samples);
}

function capture(ctx, input, { onLevel, signal }) {
  return new Promise((resolve, reject) => {
    let recorder;
    try { recorder = new MediaRecorder(input); } catch (e) { return reject(new Error('mic_unsupported')); }
    const source = ctx.createMediaStreamSource(input);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const chunks = [];
    const t0 = Date.now();
    let spokeMs = 0, quietMs = 0, last = t0, stopped = false;

    const stop = () => { if (stopped) return; stopped = true; try { recorder.stop(); } catch {} };
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now(), dt = now - last;
      last = now;
      onLevel?.(Math.min(1, rms * 8));
      if (rms > SPEECH_RMS) { spokeMs += dt; quietMs = 0; } else if (spokeMs) quietMs += dt;
      const elapsed = now - t0;
      if ((spokeMs && quietMs >= QUIET_MS) || elapsed >= MAX_MS || (!spokeMs && elapsed >= WAIT_MS)) stop();
    }, 50);

    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    recorder.onerror = () => { clearInterval(timer); reject(new Error('mic_unsupported')); };
    recorder.onstop = () => {
      clearInterval(timer);
      try { source.disconnect(); } catch {}
      onLevel?.(0);
      resolve(spokeMs < MIN_SPEECH_MS ? null : new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
    };
    signal?.addEventListener('abort', stop, { once: true });
    recorder.start();
  });
}

/** Decode whatever the browser recorded (webm, ogg, mp4) and bring it to 16 kHz mono. */
async function toMono16k(ctx, blob) {
  const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  try {
    const offline = new OAC(1, Math.ceil(audio.duration * RATE), RATE);
    const src = offline.createBufferSource();
    src.buffer = audio;
    src.connect(offline.destination);
    src.start();
    return (await offline.startRendering()).getChannelData(0);
  } catch {
    // An engine that will not render offline at 16 kHz: average each output sample's span by hand.
    const from = audio.getChannelData(0), step = audio.sampleRate / RATE;
    const out = new Float32Array(Math.floor(from.length / step));
    for (let i = 0; i < out.length; i++) {
      const a = Math.floor(i * step), b = Math.max(a + 1, Math.floor((i + 1) * step));
      let sum = 0;
      for (let j = a; j < b; j++) sum += from[j];
      out[i] = sum / (b - a);
    }
    return out;
  }
}

/** Cut the silence around the voice, leaving a short pad so no consonant is clipped. */
function trimSilence(samples) {
  const win = Math.round(RATE * TRIM_WIN), pad = Math.round(RATE * TRIM_PAD);
  const loud = (at) => {
    let sum = 0;
    for (let i = at; i < at + win; i++) sum += samples[i] * samples[i];
    return Math.sqrt(sum / win) > TRIM_RMS;
  };
  let a = 0, b = samples.length;
  while (a + win < b && !loud(a)) a += win;
  while (b - win > a && !loud(b - win)) b -= win;
  return samples.subarray(Math.max(0, a - pad), Math.min(samples.length, b + pad));
}

let lame = null;
/** The vendored MP3 encoder (public/vendor/lamejs.min.js), fetched on the first recording. */
function loadLame() {
  lame = lame || new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${process.env.PUBLIC_URL}/vendor/lamejs.min.js`;
    s.onload = () => (window.lamejs?.Mp3Encoder ? resolve(window.lamejs) : reject(new Error('lame_missing')));
    s.onerror = () => reject(new Error('lame_missing'));
    document.head.appendChild(s);
  });
  lame.catch(() => { lame = null; });
  return lame;
}

async function encode(samples) {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  try {
    const { Mp3Encoder } = await loadLame();
    const enc = new Mp3Encoder(1, RATE, MP3_KBPS);
    const frames = [];
    for (let i = 0; i < pcm.length; i += 1152) frames.push(enc.encodeBuffer(pcm.subarray(i, i + 1152)));
    frames.push(enc.flush());
    return { audioBase64: await base64(new Blob(frames, { type: 'audio/mpeg' })), format: 'mp3' };
  } catch {
    return { audioBase64: await base64(wav(pcm)), format: 'wav' };   // the encoder would not load: WAV still works
  }
}

function wav(pcm) {
  const view = new DataView(new ArrayBuffer(44));
  const str = (at, s) => { for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i)); };
  const bytes = pcm.length * 2;
  str(0, 'RIFF'); view.setUint32(4, 36 + bytes, true); str(8, 'WAVE');
  str(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, RATE, true); view.setUint32(28, RATE * 2, true); view.setUint16(32, 2, true);
  view.setUint16(34, 16, true); str(36, 'data'); view.setUint32(40, bytes, true);
  return new Blob([view.buffer, pcm.buffer], { type: 'audio/wav' });
}

function base64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
